/**
 * "Live safe" — the lot-driven trigger for the close-time schedule freeze
 * (BL-1421; [ADR 036](../../../../../docs/adrs/036_MUTATION_DRIVEN_CASCADES_AND_REALTIME.md)
 * §5 as amended 2026-08-26).
 *
 * Replaces BL-1268's `isScheduleLocked({ openedAt })`, which asked the AUCTION
 * row whether it had been stamped. That was the wrong question asked of the
 * wrong entity: *"don't move clocks while a sale is running"* is a statement
 * about **lots**, not about a timestamp. Keying on `opened_at` froze an auction
 * whose lots were all still drafts, and — because the client twin keyed on the
 * derived *phase* instead — also disabled the stagger control during
 * pre-bidding, where the server would happily have accepted the edit.
 *
 * An auction is **live safe** when at least one of its lots has reached a phase
 * where a real person is tracking its number or its outcome. Everything the
 * lock GUARDS is unchanged (see "What this does NOT gate" below); only what
 * turns it on moves.
 *
 * This is an IsomorphicService (ADR 010 / `sharing.md` §3): pure, no I/O, no
 * auth enforcement, client-safe, safe inside a `useMemo`. Typed on the minimal
 * `{ phase: { kind: LotPhaseKind } }` shape per `sharing.md` §3 rule 2, matching
 * every sibling in `lot-phase-predicates.ts` — so a caller holding a full
 * stamped `LotPhase`, or a repo row with a dozen other columns, passes it
 * straight through without fabricating fields or casting.
 *
 * **Its SQL mirror lives in `phase-where.ts`** (`liveSafeLotExistsSql` /
 * `liveSafePhaseAggregatesSql`), reachable only via the
 * `@auctogon/core/services/lifecycle/sql` subpath — that module imports
 * `@auctogon/db/schema`, which is `server-only`, and `client-safe-barrel.test.ts`
 * fails the build if it ever becomes reachable from this barrel. Both mechanisms
 * map over {@link LIVE_SAFE_LOT_KINDS}, which is the SINGLE declaration of the
 * set (BL-1421 AC 9); the parity gate
 * (`__tests__/phase-where-parity.integration.test.ts`) pins them together over a
 * fixture spanning all twelve phase kinds so they cannot fork.
 */
import type { LotPhaseKind } from './phase-tokens';

/**
 * The lot phases that make an auction's schedule untouchable.
 *
 * Exactly {@link LOT_PHASE_KINDS} minus `draft`, `ready`, `pre-bidding`,
 * `withdrawn` and `deleted`. Two of those exclusions are deliberate decisions
 * rather than fallout, and both are load-bearing:
 *
 *  - **`pre-bidding` is NOT live safe.** Nothing has closed and no number is
 *    being called, so the schedule stays fluid and the stagger control stays
 *    enabled (AC 3). This is the reversal that closes the client/server
 *    divergence described in the module docblock.
 *  - **`withdrawn` is NOT live safe.** A withdrawn lot is out of the sale and
 *    nobody is tracking its number (BL-1412 AC 9's reasoning, preserved).
 *
 * Adding or removing a member changes when operators lose the ability to edit a
 * schedule, which is a lifecycle-behaviour change under
 * `lifecycle-change-consent.md` — it needs an explicit sign-off and an ADR 036
 * re-sign, not a quiet edit.
 */
export const LIVE_SAFE_LOT_KINDS: ReadonlySet<LotPhaseKind> = new Set<LotPhaseKind>([
  'open-for-bidding',
  'last-call',
  'awaiting-resolution',
  'paused',
  'sold',
  'unsold',
]);

/**
 * Whether this auction's close-time schedule is frozen.
 *
 * `some`, not `every`: one live lot is enough. A 999-draft auction with a single
 * sold lot is live safe, because that one lot's number is being tracked by a
 * real buyer and renumbering around it would move what they were shown.
 *
 * An auction with **no lots** is NOT live safe — `some` over an empty array is
 * `false`, and that is the correct answer rather than an accident: an empty
 * auction has nothing to protect. Defaulting to "locked" here would be the
 * safe-LOOKING bug that freezes every newly-created auction.
 *
 * **What this does NOT gate.** The freeze covers automatic re-derivation — a
 * clock moved as a SIDE EFFECT of an unrelated write. It never gates the
 * transition services' legitimate clock writes — Change Close Time (BUG-1194),
 * Reopen (BL-1169 / BL-1289's `reopen` `TransitionDef`), anti-snipe extensions —
 * nor the `'fill-null'` policy, which writes only lots whose `close_time` IS
 * NULL and so cannot move a stamped clock (a lot added mid-sale still gets one).
 * Those paths must never consult this predicate.
 *
 * **Nor does it gate a DELIBERATE operator re-derive**, which is the half of
 * this sentence that has moved twice. It used to name "the three operator-facing
 * schedule EDITORS (the auction schedule-input PATCH, the ring end-time PUT, the
 * stagger PATCH)" as things the freeze refuses. Two of those three are gone:
 * BUG-1528 made the auction-level schedule edit a `warn` that recomputes through
 * `recalculateCloseTimesForScheduleChange`, and BL-1824 put the ring's close time
 * behind a CONSENT DOOR ({@link LIVE_RENUMBER_RING_RETIME_GESTURE}) that
 * restaggers that ring's unclosed lots through
 * `recalculateCloseTimesForRingRetime`. What the predicate still decides for
 * both is *whether the operator is asked*, not whether the write is allowed.
 *
 * @param lots Every lot in the auction — the WHOLE set, not a page window. The
 *   Ordering table pages at 50 (BL-1180), so a caller passing only the mounted
 *   rows would answer "not live safe" for a sale whose sold lot sits on page 3.
 *   That is why the signal is computed server-side and delivered on
 *   `LotOrderPageDto` rather than derived in the browser.
 * @returns `true` when no automatic path may recompute a close time and the
 *   schedule editors must refuse.
 * @sideEffect none — pure.
 */
export function isLiveSafe(
  lots: readonly { phase: { kind: LotPhaseKind } }[],
): boolean {
  return lots.some((lot) => LIVE_SAFE_LOT_KINDS.has(lot.phase.kind));
}

/**
 * The operator-facing explanation for every RING-ROSTER refusal (BL-1549).
 *
 * Fired off the {@link isLiveSafe} predicate. The 409 body and the disabled
 * control's tooltip are two spellings of ONE refusal, and two spellings read to
 * an operator as two checks disagreeing — so there is one string.
 *
 * **This is the only lot-driven REFUSAL string left.** Its sibling, the
 * auction-wide schedule lock reason, was deleted in BL-1687 when the schedule
 * band (the dates, the zone, and the stagger pair) became a CAUTION keyed on
 * the same predicate — those sentences live beside the evaluator in
 * `auction-field-editability.ts`.
 *
 * **What it still covers is ONE write, not BL-1549's original four**, and the
 * sentence is narrowed to say so rather than left to over-claim:
 *
 *  - **delete a ring** — re-homes every one of its lots into a neighbour,
 *    re-sequences the rings below it, and re-ranks `auction_snapshot.ring_index`
 *    live on the clerk console. Still refused outright (BL-1825 declined to open
 *    it, and BL-1824 declined again: three blast radii with no clean consent
 *    sentence between them).
 *
 * The other three left, each to a CONSENT door rather than by re-opening a hole:
 * `startingLotNumber` in BUG-1524 (`resolveLiveSafeRingStart`, the renumber
 * door); **adding a ring in BL-1825** — an appended ring is empty at birth and
 * carries no start, so it moves nothing, and the one shape that could pair an
 * add with a start in one transaction takes BUG-1524's door; and **a ring's
 * close time in BL-1824** ({@link LIVE_RENUMBER_RING_RETIME_GESTURE}), which
 * restaggers only that ring's unclosed lots once the operator has answered.
 *
 * **Keep this sentence honest about that.** It used to read "so rings cannot be
 * changed", which after BL-1825 sat in a 409 two inches from an enabled "Add
 * ring" button — a refusal that contradicts the screen it is shown on teaches an
 * operator to distrust both. BL-1824 removed its close-time clause for the same
 * reason: that picker is now enabled too.
 *
 * Deliberately ONE sentence with no premium clause. The Configure rings modal
 * appends its own "you can still…" line because the exemptions are facts about
 * that screen's other controls; an API caller refused a ring DELETE does not
 * need them, and baking them in here would put them in the 409 body of routes
 * that never touch a premium.
 *
 * **What this does NOT cover.** A ring's buyer's premium (set through the
 * Configure rings save, `PUT /rings/roster`) is exempt by design — correcting a
 * premium mid-sale is legitimate, it is guarded by its own warn-don't-block
 * edit-impact count (BL-1425), and the server has always accepted it.
 */
export const RING_LOCK_REASON =
  'This auction has lots that are live or already sold, so rings cannot be removed.';

/**
 * Which door is refusing a live renumber, in the two clauses an operator reads
 * (BUG-1523).
 *
 * The three fields are the three clauses of one sentence: "This auction has lots
 * that are live or already sold, and this **<noun>** would **<consequence>**.
 * **<remedy>**". All three vary per door, because the doors differ in more than
 * a word — the reorder sends the operator to a different SCREEN, the ring
 * change is already on the right one, and each sizes its blast radius
 * differently. **None of them shifts a closing time** (BUG-1675 — this sentence
 * used to claim the reorder did). Only the opening clause — the CAUSE — is
 * shared, and that is the half an operator must read identically from all
 * three, which is why this is one formatter with variants rather than three
 * strings.
 *
 * Declared here beside the formatter rather than at the call sites, so a fourth
 * door is a constant in this file rather than a fourth sentence somewhere else.
 *
 * **BL-1824 folded the CONFIRMATION copy in beside the refusal copy.** The three
 * `confirm*` fields below are what `LiveRenumberWarning` renders; before this the
 * modal hard-coded "Renumber the live lots?" / "Renumber", which is simply false
 * of the ring RE-TIME door — it moves closing times and renumbers nobody. One
 * named variant now owns everything an operator reads about a door, whether they
 * met it in a dialog or in a 409 body (ADR 044 — Must Consolidate: the refusal
 * and the confirmation are two spellings of one decision, so they get one owner).
 */
export interface LiveRenumberGesture {
  /** The gesture the caller asked for, as it reads mid-sentence. */
  noun: string;
  /**
   * The confirmation dialog's title — a question, because the dialog asks one.
   *
   * BL-1824. Gesture-owned for the same reason `consequence` is: the three
   * renumber doors ask about lot NUMBERS and the retime door asks about CLOSING
   * TIMES, and one shared title would be wrong for one of them.
   */
  confirmTitle: string;
  /** The confirmation dialog's lead sentence, given the blast-radius total. */
  confirmLead: (affected: number) => string;
  /** The confirmation dialog's submit label — the verb the operator is agreeing to. */
  confirmSubmitLabel: string;
  /**
   * What it would DO, given the blast-radius count — the clause between the
   * noun and the remedy.
   *
   * Gesture-owned rather than shared, because the three doors size their blast
   * radius differently and a shared clause makes one of them LIE — the reorder
   * counts the lots the cascade MOVES, while the two ring doors count the
   * live/sold population of the whole AUCTION.
   *
   * **No RENUMBER door carries a closing-time clause, and none may gain one**
   * (BUG-1523 AC 8, extended to the reorder by BUG-1675):
   * `lot-close-time-service.ts` refuses the re-stagger on a live-safe auction
   * for those paths, and a live-safe auction is the ONLY auction any of these
   * refusals is produced on. So a renumber through the reorder / ring-move /
   * ring-start doors moves numbers and nothing else. A refusal that over-states
   * the damage is not a safer refusal, it is a wrong one an operator learns to
   * discount.
   *
   * **BL-1824's RETIME door is the exact inverse, and that is why it is a
   * separate gesture rather than a fourth caller of an existing one.** It moves
   * closing times and renumbers nobody, because its consented write reaches
   * `recalculateCloseTimesForRingRetime` — the lock-exempt recompute — instead of
   * being refused by the lock. The sentence above is therefore scoped to the
   * renumber family rather than deleted: for those three it is still true, still
   * load-bearing, and still the thing BUG-1675 had to correct.
   */
  consequence: (affected: number) => string;
  /** Where the operator CAN take it — a full sentence, never a fragment. */
  remedy: string;
}

/** `N lot` / `N lots`, the pluralization both consequences need. */
function lotCount(n: number): string {
  return `${n} ${n === 1 ? 'lot' : 'lots'}`;
}

/** The reorder door (BUG-1429) — `PUT /api/auctions/[id]/lots/reorder`. */
export const LIVE_RENUMBER_REORDER_GESTURE: LiveRenumberGesture = {
  noun: 'reorder',
  confirmTitle: 'Renumber the live lots?',
  confirmLead: (affected) =>
    `The lot numbers for ${affected} live ${affected === 1 ? 'lot' : 'lots'} will be `
    + 'affected by this change:',
  confirmSubmitLabel: 'Renumber',
  // BUG-1675: deliberately no closing-time clause, matching BOTH sibling doors
  // and for the same reason — see `consequence`'s note above. This gesture is
  // the last of the three to lose it: the clause was written when the reorder
  // was believed to be the door that DID move clocks, and was not swept when
  // BUG-1523 established otherwise.
  consequence: (affected) => `renumber ${lotCount(affected)}`,
  remedy: 'Reorder it from the Ordering screen, which asks before renumbering a running sale.',
};

/**
 * The bulk ring door (BUG-1523) — `POST /api/lots/batch` with
 * `{ type: 'reassign-ring' }`, either arm.
 *
 * Its remedy names the CONTROL rather than a screen. When it was written the
 * gesture had two homes — the Ordering tab's "Ring" picker and the Lots tab's
 * "Move" picker, one control under two labels; BL-1905 retired the "Move" one,
 * but naming the control stays right. What the operator is missing is not a
 * destination — it is the confirmation the bulk bar shows before it sends, which
 * is what the sentence points at. That also makes it true for a direct API
 * caller, who was never on a screen at all, and for any surface that adds the
 * picker later.
 */
export const LIVE_RENUMBER_RING_MOVE_GESTURE: LiveRenumberGesture = {
  noun: 'ring change',
  // The three renumber doors deliberately SHARE this confirmation copy: an
  // operator meeting the dialog through any of them is agreeing to the same
  // thing (lot numbers move). They are repeated per gesture rather than
  // defaulted so that a new door has to state what it is asking about — which
  // is exactly the step BL-1824's retime door needed and would have skipped.
  confirmTitle: 'Renumber the live lots?',
  confirmLead: (affected) =>
    `The lot numbers for ${affected} live ${affected === 1 ? 'lot' : 'lots'} will be `
    + 'affected by this change:',
  confirmSubmitLabel: 'Renumber',
  // Deliberately says "is being tracked by bidders on" rather than "would
  // renumber N lots": the count is the live/sold population of the AUCTION, not
  // the size of the selection, and the ring door renumbers only what was picked.
  // Borrowing the reorder's phrasing would name a number and mean a different
  // one. And no closing-time clause — see `consequence`'s note above.
  consequence: (affected) =>
    'give the lots you picked new numbers, on an auction where bidders are '
    + `tracking ${lotCount(affected)}`,
  // Names the CONTROL, never a screen. The reorder's remedy can name one
  // because that gesture has a single home; this one had two when the sentence
  // was written (the Lots tab's "Move" picker, since retired by BL-1905, beside
  // the Ordering tab's "Ring" picker), and naming either screen sent half the
  // operators somewhere they were not. Wiring another surface must not require
  // editing this sentence.
  remedy:
    'Use the bulk ring picker, which asks you to confirm the new lot numbers '
    + 'before anything is written.',
};

/**
 * The ring-START door (BUG-1524) — the `startingLotNumber` half of the
 * Configure rings modal's roster `PUT` (the single-ring
 * `PUT /api/auctions/[id]/rings/[ringId]` that shared it was deleted in
 * BL-1833 PR 3c-1).
 *
 * The third door on one mechanism, and the one with the widest reach: re-basing
 * a ring re-deals every lot number from that ring down, in one write. BL-1549
 * refused it outright as part of the ring-roster freeze; BUG-1524 moved it here
 * instead, because refusing removed a capability operators legitimately need
 * mid-sale rather than making it deliberate (PRJ-45's signed "nothing blocks /
 * warn loudly").
 *
 * Its `noun` names the FIELD, not the screen, for the same reason the ring-move
 * door names a control: this gesture has two callers — the modal and the
 * per-ring `PUT` a direct API client uses — and naming either would be wrong for
 * the other.
 *
 * Deliberately no closing-time clause, matching the ring-move door and for the
 * same reason: `lot-close-time-service.ts` refuses the re-stagger on a live-safe
 * auction, so a renumber here moves numbers and nothing else. A refusal that
 * over-states the damage is not a safer refusal — it is a wrong one an operator
 * learns to discount.
 */
export const LIVE_RENUMBER_RING_START_GESTURE: LiveRenumberGesture = {
  noun: "change to a ring's starting lot number",
  confirmTitle: 'Renumber the live lots?',
  confirmLead: (affected) =>
    `The lot numbers for ${affected} live ${affected === 1 ? 'lot' : 'lots'} will be `
    + 'affected by this change:',
  confirmSubmitLabel: 'Renumber',
  // Says "re-deal … below it" rather than borrowing the reorder's "renumber N
  // lots": the count is the live/sold population of the AUCTION, and the lots
  // this actually moves are the ones at or below the ring whose start changed.
  // Naming a number and meaning a different one is the mistake BUG-1523 caught
  // on the ring-move door.
  consequence: (affected) =>
    're-deal the lot numbers from that ring down, on an auction where bidders '
    + `are tracking ${lotCount(affected)}`,
  remedy:
    'Configure rings asks you to confirm the renumber before anything is written.',
};

/**
 * The ring RE-TIME door (BL-1824) — the `endTime` half of the Configure rings
 * modal's roster `PUT`. (The single-ring and batch ring `PUT`s that shared it
 * were deleted in BL-1833 PR 3c-1.)
 *
 * The FOURTH door on one mechanism, and the first that is not about lot numbers
 * at all. BL-1549 refused a ring's close time outright on the grounds that *"a
 * stamped close time can never be recomputed again, so a new `end_time` would
 * disagree with every lot's clock permanently — there is nothing to confirm,
 * only damage to prevent."* Both of that argument's neighbours falsified it:
 * `ChangeCloseTimeService` moves a single lot's stamped close on a live sale,
 * and BUG-1528's `recalculateCloseTimesForScheduleChange` recomputes every
 * unclosed lot's. BL-1824 supplies the missing middle rung — a lock-exempt
 * recompute SCOPED TO ONE RING — behind this door (ADR 036 — Mutation Cascades
 * §5 re-sign, Michael Lawrie 2026-09-21).
 *
 * Its `noun` names the FIELD rather than a screen, for the same reason its two
 * ring siblings do: three callers reach it (the per-ring PUT, the roster PUT,
 * the batch PUT), so naming any one screen is wrong for the other two.
 *
 * **It is the one gesture that DOES carry a closing-time clause**, and must —
 * see `consequence`'s note above. The other three are refusals produced on an
 * auction where the re-stagger is refused anyway; this one is a refusal produced
 * on an auction where answering it WILL restagger. Over-stating damage teaches an
 * operator to discount a refusal; under-stating it is worse.
 *
 * Its blast radius is the narrowest of the four, and that is deliberate rather
 * than incidental: the other three count the live/sold population of the whole
 * AUCTION because a renumber's reach is not knowable up front, while a retime's
 * is — `countRingRetimeSummary` counts exactly the lots of THIS RING that have
 * not closed, which is exactly the set `recalculateCloseTimesForRingRetime`
 * writes (AC 1).
 */
export const LIVE_RENUMBER_RING_RETIME_GESTURE: LiveRenumberGesture = {
  noun: "change to a ring's close time",
  consequence: (affected) =>
    `move the closing times of ${lotCount(affected)} in that ring that have not closed yet`,
  remedy:
    'Configure rings asks you to confirm the new closing times before anything is written.',
  confirmTitle: 'Move the closing times?',
  confirmLead: (affected) =>
    `Closing times will move for ${lotCount(affected)} in this ring that ${affected === 1 ? 'has' : 'have'} `
    + 'not closed yet. Lots that have already sold or passed keep the time they closed at, and so '
    + 'does any lot an anti-snipe extension has already moved:',
  confirmSubmitLabel: 'Move closing times',
};

/**
 * The RESTORE door (BL-1848) — `POST /api/lots/batch` with `{ type: 'restore' }`
 * on a live-safe auction whose deleted lot's old number a live lot now holds.
 *
 * The fifth caller of the mechanism, and the second of the `'placement'` door:
 * it makes the ring move's promise exactly — the restored lot takes the next free
 * number at the end of its ring and NO other lot's number changes — so it spends
 * the same consent rather than a new one (ADR 044 — Must Consolidate). Its words
 * are its own because the shared renumber confirmation ("the lot numbers for N
 * live lots will be affected") is false here: only the restored lot is numbered.
 *
 * The remedy names the status menu's Restore row, the one entry point for a
 * lifecycle transition (rule #38 §6), which is where the confirmation lives.
 *
 * No closing-time clause, for the renumber family's reason: the schedule lock
 * refuses the re-stagger on a live-safe auction, the only auction this refusal
 * is produced on (BUG-1523 AC 8 / BUG-1675).
 */
export const LIVE_RENUMBER_RESTORE_GESTURE: LiveRenumberGesture = {
  noun: 'restore',
  confirmTitle: 'Restore with a new lot number?',
  confirmLead: (affected) =>
    "Another lot now holds this lot's old number, so it will get the next free number "
    + "at the end of its ring. No other lot's number will change. Bidders are tracking "
    + `${affected} live ${affected === 1 ? 'lot' : 'lots'} on this auction:`,
  confirmSubmitLabel: 'Restore with new number',
  consequence: (affected) =>
    'give the restored lot a new number at the end of its ring, on an auction where '
    + `bidders are tracking ${lotCount(affected)}`,
  remedy:
    "Restore it from the lot's status menu, which asks you to confirm the new lot "
    + 'number before anything is written.',
};

/**
 * How a renumber's blast radius is bucketed — the SINGLE declaration of which
 * lot phases are counted, what each bucket is CALLED, and what order they read
 * in (BUG-1588).
 *
 * Everything downstream is built from this list: the type below, the SQL
 * aggregate ({@link liveSafePhaseAggregatesSql} in `phase-where.ts`), the
 * repository's one-row summary, and the warning modal's rendered lines. Adding a
 * bucket here is the whole edit; nothing else enumerates them.
 *
 * ## Why it is a declaration rather than three hand-written counts
 *
 * BUG-1588. The buckets used to be three `countOf([...])` calls written out
 * beside {@link LIVE_SAFE_LOT_KINDS} but derived from nothing, and the two sets
 * drifted: the kind set says whether the interception OPENS, the buckets say
 * what it REPORTS, and the four `Live`-group kinds — `open-for-bidding`,
 * `last-call`, `awaiting-resolution`, `paused` — ended up in the first and not
 * the second. So the single commonest live shape, a sale whose lots are simply
 * open for bidding, triggered the question and then answered it with
 * *"0 live lots will be affected"* immediately before re-dealing the catalog.
 *
 * `blast-radius-buckets-cover-live-safe-kinds` in `__tests__/live-safe.test.ts`
 * pins the union of `kinds` here to `LIVE_SAFE_LOT_KINDS ∪ {'pre-bidding'}`, so
 * a kind can never again open the interception without being counted by it.
 *
 * ## Why `pre-bidding` is bucketed but is not live-safe
 *
 * Deliberate, and the two facts are not in tension. A pre-bidding lot never makes
 * an auction live-safe on its own (BL-1421 — nothing has closed and no number is
 * being called). But once some OTHER lot has opened the question, a bidder who
 * placed an advance bid is tracking that lot's number like anyone else, so it is
 * counted. Hence union-with, not equal-to.
 *
 * ## Labels
 *
 * Operator vocabulary, lowercased from `LOT_PHASE_LABELS` — the words already on
 * every lot's status pill ("Sale", "No Sale", "Accepting bids"), so the modal
 * cannot invent a fourth spelling of a phase the rest of the product has named.
 * `openForBidding` is deliberately NOT labelled "live": the warning's own total
 * sentence already spends that word on the whole population.
 */
export const LIVE_RENUMBER_BLAST_RADIUS_BUCKETS = [
  { key: 'preBidding', label: 'pre-bidding', kinds: ['pre-bidding'] },
  {
    key: 'openForBidding',
    label: 'open for bidding',
    // Every `Live`-group kind, folded into one bucket. `last-call` and
    // `awaiting-resolution` are the same expired-clock state split by the
    // auction's closing type, and `paused` is a lot whose bidding is halted —
    // none of the three is a terminal outcome, and an operator reading a blast
    // radius needs "how many are still in play", not a four-way split of it.
    kinds: ['open-for-bidding', 'last-call', 'awaiting-resolution', 'paused'],
  },
  { key: 'sale', label: 'sale', kinds: ['sold'] },
  // `noSale` is `unsold` ALONE. It used to fold `unsold` AND `passed`; BL-1387
  // PR 2 retired the `passed` phase token (no production writer ever stamped
  // `passed_at`), so the fold has one member and the second is gone rather than
  // silently counting zero. A reserve-not-met lot derives `unsold` and is
  // counted here exactly as before.
  { key: 'noSale', label: 'no sale', kinds: ['unsold'] },
] as const satisfies readonly {
  key: string;
  label: string;
  kinds: readonly LotPhaseKind[];
}[];

/** One bucket's key — the property names of {@link LiveRenumberBlastRadius}. */
export type LiveRenumberBucketKey = (typeof LIVE_RENUMBER_BLAST_RADIUS_BUCKETS)[number]['key'];

/**
 * The lots a renumber would touch, bucketed by what those bidders are doing.
 *
 * The client's `LiveSafeBlastRadius`, the `lotPhaseCounts` on `LotOrderPageDto`,
 * and the server's `countLotPhaseSummaryForOrder` counts — declared here, once,
 * because BUG-1429 made both ends of the same interception carry it: the browser
 * renders these numbers in the warning, and the server's 409 names them for a
 * caller that never saw the warning.
 *
 * DERIVED from {@link LIVE_RENUMBER_BLAST_RADIUS_BUCKETS} rather than written
 * out, so a bucket added there is a compile error at every site that builds one
 * rather than a silent zero (BUG-1588).
 *
 * `LiveSafeBlastRadius` was a STRUCTURAL twin of this until BUG-1524, and
 * `LotOrderPageDto.lotPhaseCounts` until BUG-1588; both are plain aliases now. A
 * second declaration of numbers that must agree is how they stop agreeing.
 */
export type LiveRenumberBlastRadius = Record<LiveRenumberBucketKey, number>;

/**
 * The total across every bucket — the number the operator reads first.
 *
 * Exported because the 409 formatter and the warning modal both need it and
 * neither may sum the keys itself: a hand-written sum is precisely what silently
 * stops counting a bucket added later.
 *
 * @param blastRadius The bucketed counts.
 * @returns How many lots the renumber would move.
 * @sideEffect none — pure.
 */
export function totalLiveRenumberAffected(blastRadius: LiveRenumberBlastRadius): number {
  return LIVE_RENUMBER_BLAST_RADIUS_BUCKETS.reduce((sum, b) => sum + blastRadius[b.key], 0);
}

/**
 * The single operator-facing explanation for a refused live renumber (BUG-1429).
 *
 * The sibling of {@link RING_LOCK_REASON}, and here for the same reason: the
 * server's 409 and the browser's warning are two spellings of ONE refusal, and
 * two spellings read to an operator as two checks disagreeing. Kept a formatter
 * rather than a constant because the count is the whole point — "22 live lots
 * will be renumbered" is actionable, "this auction is live" is not.
 *
 * Deliberately says what WOULD happen rather than what is forbidden: the
 * renumber is legal, and the operator can take it by answering the interception
 * (which sends `acknowledgedDoor`). This text is what a caller who never
 * saw that question gets instead.
 *
 * **The gesture is a named variant, not caller freedom (BUG-1523).** This used
 * to hardcode "this *reorder*… Reorder it from the Ordering screen", which was
 * true while the reorder door was the only one enforcing the interception. It
 * is not true of the bulk ring picker, whose caller would be told to go and do
 * something it never asked to do. So the two clauses an operator reads as
 * *which gesture* and *where to take it* are inputs — supplied from the named
 * constants below, never composed at a call site, so there is exactly one
 * sentence with two data values rather than two sentences
 * (ADR 044 — Must Consolidate, "intentional divergence lives inside the choke
 * point"). Deliberately has no default: a caller says WHICH surface it is, and
 * a default would let a new door silently ship the reorder's remedy.
 *
 * @param blastRadius The lots the cascade would move, by phase.
 * @param gesture Which door is refusing, and where the operator can take it.
 * @returns One sentence naming the cause, the consequence, and the size.
 * @sideEffect none — pure.
 */
export function formatLiveRenumberRefusal(
  blastRadius: LiveRenumberBlastRadius,
  gesture: LiveRenumberGesture,
): string {
  const affected = totalLiveRenumberAffected(blastRadius);
  return (
    `This auction has lots that are live or already sold, and this ${gesture.noun} would `
    + `${gesture.consequence(affected)}. `
    + `${gesture.remedy}`
  );
}

/**
 * Which door of the live-safe interception the operator answered (BUG-1550).
 *
 * The interception offers three, and they promise different things:
 *
 *  - `'placement'` — "Insert as a suffixed lot", which promises *"No other lot
 *    number changes."* Consent for the mover to take one specific free pair.
 *  - `'renumber'` — "Insert here and renumber", shown with a blast radius.
 *    Consent for everything below the insert point to shift.
 *  - `'retime'` — "Move the closing times" (BL-1824), shown with a ring-scoped
 *    blast radius. Consent for that ring's unclosed lots to restagger off a new
 *    base. It is NOT interchangeable with `'renumber'`: each opens exactly one
 *    door, so a `'retime'` consent can never be spent on a re-deal of the
 *    catalog and a `'renumber'` consent can never move a clock. That mutual
 *    exclusivity is the same guarantee BUG-1550 bought for the first two, and it
 *    is enforced structurally — `resolveLiveSafeRingStart` tests for
 *    `'renumber'`, `resolveLiveSafeRingRetime` tests for `'retime'`, and neither
 *    has a fallthrough.
 *
 * It replaces the `acknowledgeLiveRenumber` boolean, which could not tell them
 * apart. That mattered because the two answers are only equivalent while the
 * target pair stays free: if another operator takes the pair between the modal
 * opening and the write landing, a placement consent was being spent as consent
 * for a full catalog renumber — sold lots included — that this operator was
 * never shown a blast radius for.
 *
 * **Absent is not a third door.** It means nobody was asked, and on a live-safe
 * auction a move that rewrites a bystander is refused without it (BUG-1429).
 * Consent stays per-request by design: a session- or auction-level answer would
 * let one click cover every later reorder.
 *
 * Declared here, in the client-safe lifecycle barrel, so the hook that stamps it
 * and the service that reads it share ONE declaration rather than a twin per
 * side of the wire.
 */
export type LiveSafeAcknowledgedDoor = 'placement' | 'renumber' | 'retime';

/**
 * The wire values of {@link LiveSafeAcknowledgedDoor}, for runtime validation.
 *
 * Typed as a non-empty TUPLE rather than `readonly LiveSafeAcknowledgedDoor[]`
 * (BUG-1523) so `z.enum(...)` accepts it directly and the batch schema does not
 * need a hand-written literal union beside this one. `.includes(...)` on the
 * reorder route's hand-rolled parser is unaffected. `satisfies` keeps the pair
 * tied to the type above: drop a door from the union and this line fails, which
 * is the check a widened `string[]` would have lost.
 */
export const LIVE_SAFE_ACKNOWLEDGED_DOORS = [
  'placement',
  'renumber',
  'retime',
] as const satisfies readonly LiveSafeAcknowledgedDoor[];
